import { useState, useEffect } from "react";
import Input from "components/ui/Input";
import Select from "components/ui/Select";
import {
  Table,
  THead,
  TBody,
  TR,
  TH,
  TD,
  EmptyRow,
} from "components/ui/Table";
import {
  useCompanyPreferences,
  useUpdateCompanyPreferences,
  useCompanyDiscounts,
  useUpdateCompanyDiscounts,
  useCompanyFinancialYears,
  useUpdateCompanyFYLocking,
} from "http-hooks/companyAdmin/useCompanyAdmin";
import { useFinancialYearStore } from "store";

const salebillModeOptions = [
  { value: 0, label: "Default Only" },
  { value: 1, label: "Without Items Only" },
  { value: 2, label: "Both (User Selects)" },
];

const billNumberDigitOptions = [
  { value: 4, label: "4 digits" },
  { value: 5, label: "5 digits" },
];

export default function CompanyPreferences() {
  const currentFinancialYear = useFinancialYearStore(
    (state) => state.currentFinancialYear
  );
  const { data: prefData, isLoading: isPrefLoading } = useCompanyPreferences();
  const { data: discountData, isLoading: isDiscLoading } = useCompanyDiscounts();
  const { data: fyData, isLoading: isFYLoading } = useCompanyFinancialYears();

  const { mutateAsync: mutatePref, isPending: isSavingPref } =
    useUpdateCompanyPreferences();
  const { mutateAsync: mutateDisc, isPending: isSavingDisc } =
    useUpdateCompanyDiscounts();
  const { mutateAsync: mutateFY, isPending: isSavingFY } =
    useUpdateCompanyFYLocking();

  const [prefs, setPrefs] = useState({
    shp: 1,
    clt: 1,
    sz: 1,
    lot: 1,
    packratio: 1,
    allow_scheme_order: 0,
    salebill_mode: 2,
    salebill_template: 1,
    foreign_export: 0,
    trader_salebill_auto_number: 0,
    trader_salebill_number_prefix: "SB/",
    trader_salebill_number_include_fy: 1,
    trader_salebill_number_digits: 5,
    party_cash_limit_fy: 0,
  });

  const [templateOptions, setTemplateOptions] = useState([
    { value: 1, label: "Template 1" },
  ]);

  const [discounts, setDiscounts] = useState([]);
  const [financialYears, setFinancialYears] = useState([]);

  useEffect(() => {
    if (prefData) {
      setPrefs({
        shp: prefData.shp,
        clt: prefData.clt,
        sz: prefData.sz,
        lot: prefData.lot,
        packratio: prefData.packratio,
        allow_scheme_order: prefData.allow_scheme_order,
        salebill_mode: prefData.salebill_mode ?? 2,
        salebill_template: prefData.salebill_template ?? 1,
        foreign_export: prefData.foreign_export ?? 0,
        trader_salebill_auto_number:
          prefData.trader_salebill_auto_number ?? 0,
        trader_salebill_number_prefix:
          prefData.trader_salebill_number_prefix ?? "SB/",
        trader_salebill_number_include_fy:
          prefData.trader_salebill_number_include_fy ?? 1,
        trader_salebill_number_digits:
          prefData.trader_salebill_number_digits ?? 5,
        party_cash_limit_fy: prefData.party_cash_limit_fy,
      });
      if (prefData.salebill_template_options?.length) {
        setTemplateOptions(prefData.salebill_template_options);
      }
    }
  }, [prefData]);

  useEffect(() => {
    if (discountData) {
      setDiscounts(
        discountData.map((d) => ({
          codeid: d.codeid,
          name: d.name,
          shortname: d.shortname,
          ag_status: d.ag_status,
        }))
      );
    }
  }, [discountData]);

  useEffect(() => {
    if (fyData) {
      setFinancialYears(
        fyData.map((fy) => ({
          srno: fy.srno,
          fys: fy.fys,
          fye: fy.fye,
          acc_locked_upto: fy.acc_locked_upto || "",
          gst_locked_upto: fy.gst_locked_upto || "",
        }))
      );
    }
  }, [fyData]);

  const handleToggle = (field) => {
    setPrefs((prev) => ({ ...prev, [field]: prev[field] === 1 ? 0 : 1 }));
  };

  const handleDiscountToggle = (index) => {
    setDiscounts((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        ag_status: updated[index].ag_status === 1 ? 0 : 1,
      };
      return updated;
    });
  };

  const handleDiscountShortname = (index, value) => {
    setDiscounts((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], shortname: value };
      return updated;
    });
  };

  const handleFYChange = (index, field, value) => {
    setFinancialYears((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleSavePreferences = async () => {
    try {
      await mutatePref(prefs);
    } catch (error) {
      console.error("Save preferences failed:", error);
    }
  };

  const handleSaveDiscounts = async () => {
    try {
      await mutateDisc({
        discounts: discounts.map((d) => ({
          codeid: d.codeid,
          shortname: d.shortname,
          ag_status: d.ag_status,
        })),
      });
    } catch (error) {
      console.error("Save discounts failed:", error);
    }
  };

  const handleSaveFYLocking = async () => {
    try {
      await mutateFY({
        financialYears: financialYears.map((fy) => ({
          srno: fy.srno,
          acc_locked_upto: fy.acc_locked_upto || null,
          gst_locked_upto: fy.gst_locked_upto || null,
        })),
      });
    } catch (error) {
      console.error("Save FY locking failed:", error);
    }
  };

  const getFYLabel = (fys, fye) => {
    const startYear = fys ? new Date(fys).getFullYear() : "";
    const endYear = fye ? new Date(fye).getFullYear() : "";
    return `${startYear}-${endYear}`;
  };

  const previewStartYear = String(currentFinancialYear?.fys || "2025").slice(
    0,
    4
  );
  const previewEndYear = String(currentFinancialYear?.fye || "2026").slice(
    0,
    4
  );
  const billNumberPreview = `${
    prefs.trader_salebill_number_prefix
  }${
    prefs.trader_salebill_number_include_fy === 1
      ? `${previewStartYear.slice(-2)}-${previewEndYear.slice(-2)}/`
      : ""
  }${String(1).padStart(prefs.trader_salebill_number_digits, "0")}`;

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="md:flex md:items-center md:justify-between mb-6">
        <div className="min-w-0 flex-1">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:truncate sm:text-3xl sm:tracking-tight">
            Company Preferences
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Configure item preferences, discounts, and financial year locking
          </p>
        </div>
      </div>

      {/* Section 1: Item Preferences */}
      <div className="bg-white shadow rounded-lg mb-6">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Item Preferences
          </h3>
        </div>
        <div className="px-6 py-4">
          {isPrefLoading ? (
            <p className="text-sm text-gray-500">Loading preferences...</p>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                <Input
                  type="checkbox"
                  label="Shape"
                  value={prefs.shp === 1}
                  onChange={() => handleToggle("shp")}
                />
                <Input
                  type="checkbox"
                  label="Color"
                  value={prefs.clt === 1}
                  onChange={() => handleToggle("clt")}
                />
                <Input
                  type="checkbox"
                  label="Size"
                  value={prefs.sz === 1}
                  onChange={() => handleToggle("sz")}
                />
                <Input
                  type="checkbox"
                  label="Lot"
                  value={prefs.lot === 1}
                  onChange={() => handleToggle("lot")}
                />
                <Input
                  type="checkbox"
                  label="Item Packing Ratio"
                  value={prefs.packratio === 1}
                  onChange={() => handleToggle("packratio")}
                />
                <Input
                  type="checkbox"
                  label="Scheme Order"
                  value={prefs.allow_scheme_order === 1}
                  onChange={() => handleToggle("allow_scheme_order")}
                />
                <Input
                  type="checkbox"
                  label="Foreign Export Billing"
                  value={prefs.foreign_export === 1}
                  onChange={() => handleToggle("foreign_export")}
                />
              </div>

              <div className="border-t border-gray-200 pt-4 mt-4">
                <h4 className="text-md font-medium text-gray-900 mb-3">
                  General
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <Input
                    type="number"
                    label="Transaction Limit / AC (in FY)"
                    value={prefs.party_cash_limit_fy}
                    onChange={(e) =>
                      setPrefs((prev) => ({
                        ...prev,
                        party_cash_limit_fy: parseFloat(e.target.value) || 0,
                      }))
                    }
                    placeholder="Transaction limit per A/C in FY"
                    min={0}
                  />
                  <Select
                    label="Sale Bill Mode"
                    options={salebillModeOptions}
                    value={salebillModeOptions.find(
                      (o) => o.value === prefs.salebill_mode
                    )}
                    onChange={(val) =>
                      setPrefs((prev) => ({
                        ...prev,
                        salebill_mode: val?.value ?? 2,
                      }))
                    }
                    isClearable={false}
                    isSearchable={false}
                  />
                  <Select
                    label="Sale Bill Template"
                    options={templateOptions}
                    value={templateOptions.find(
                      (o) => o.value === prefs.salebill_template
                    )}
                    onChange={(val) =>
                      setPrefs((prev) => ({
                        ...prev,
                        salebill_template: val?.value ?? 1,
                      }))
                    }
                    isClearable={false}
                    isSearchable={false}
                  />
                </div>
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={handleSavePreferences}
                  disabled={isSavingPref}
                  className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSavingPref ? "Saving..." : "Save Preferences"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Section 2: Bill Number */}
      <div className="bg-white shadow rounded-lg mb-6">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Bill Number</h3>
          <p className="mt-1 text-sm text-gray-500">
            Shared numbering for trader sale bills and cash memos
          </p>
        </div>
        <div className="px-6 py-4">
          {isPrefLoading ? (
            <p className="text-sm text-gray-500">Loading preferences...</p>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Input
                  type="checkbox"
                  label="Automatic Numbering"
                  value={prefs.trader_salebill_auto_number === 1}
                  onChange={() =>
                    handleToggle("trader_salebill_auto_number")
                  }
                />
                <Input
                  type="text"
                  label="Prefix"
                  value={prefs.trader_salebill_number_prefix}
                  onChange={(e) =>
                    setPrefs((prev) => ({
                      ...prev,
                      trader_salebill_number_prefix: e.target.value,
                    }))
                  }
                  maxLength={9}
                  placeholder="SB/"
                />
                <Input
                  type="checkbox"
                  label="Include Financial Year"
                  value={prefs.trader_salebill_number_include_fy === 1}
                  onChange={() =>
                    handleToggle("trader_salebill_number_include_fy")
                  }
                />
                <Select
                  label="Sequence Digits"
                  options={billNumberDigitOptions}
                  value={billNumberDigitOptions.find(
                    (option) =>
                      option.value === prefs.trader_salebill_number_digits
                  )}
                  onChange={(option) =>
                    setPrefs((prev) => ({
                      ...prev,
                      trader_salebill_number_digits: option?.value ?? 5,
                    }))
                  }
                  isClearable={false}
                  isSearchable={false}
                />
              </div>

              <div className="mt-4 rounded-md border border-indigo-200 bg-indigo-50 px-4 py-3">
                <p className="text-sm text-indigo-700">Format example</p>
                <p className="mt-1 font-mono text-lg font-semibold text-indigo-900">
                  {billNumberPreview}
                </p>
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={handleSavePreferences}
                  disabled={isSavingPref}
                  className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSavingPref ? "Saving..." : "Save Bill Number Settings"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Section 3: Discounts */}
      <div className="bg-white shadow rounded-lg mb-6">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Discounts</h3>
        </div>
        <div className="px-6 py-4">
          {isDiscLoading ? (
            <p className="text-sm text-gray-500">Loading discounts...</p>
          ) : (
            <>
              <Table>
                <THead>
                  <TR>
                    <TH first>#</TH>
                    <TH>Discount Name</TH>
                    <TH>Enabled</TH>
                    <TH last>Short Name</TH>
                  </TR>
                </THead>
                <TBody>
                  {discounts.length === 0 ? (
                    <EmptyRow colSpan={4} message="No discounts found" />
                  ) : (
                    discounts.map((disc, index) => (
                      <TR key={disc.codeid}>
                        <TD first>{index + 1}</TD>
                        <TD>{disc.name}</TD>
                        <TD>
                          <Input
                            type="checkbox"
                            showLabel={false}
                            value={disc.ag_status === 1}
                            onChange={() => handleDiscountToggle(index)}
                          />
                        </TD>
                        <TD last>
                          <Input
                            type="text"
                            showLabel={false}
                            value={disc.shortname}
                            onChange={(e) =>
                              handleDiscountShortname(index, e.target.value)
                            }
                            maxLength={10}
                            placeholder="Short Name"
                          />
                        </TD>
                      </TR>
                    ))
                  )}
                </TBody>
              </Table>

              {discounts.length > 0 && (
                <div className="mt-4 flex justify-end">
                  <button
                    type="button"
                    onClick={handleSaveDiscounts}
                    disabled={isSavingDisc}
                    className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSavingDisc ? "Saving..." : "Save Discounts"}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Section 3: Financial Year Data Locking */}
      <div className="bg-white shadow rounded-lg mb-6">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Data Locking (For each Financial Year)
          </h3>
        </div>
        <div className="px-6 py-4">
          {isFYLoading ? (
            <p className="text-sm text-gray-500">
              Loading financial years...
            </p>
          ) : (
            <>
              <Table>
                <THead>
                  <TR>
                    <TH first>#</TH>
                    <TH>Financial Year</TH>
                    <TH>Acc Locked Upto</TH>
                    <TH last>GST Locked Upto</TH>
                  </TR>
                </THead>
                <TBody>
                  {financialYears.length === 0 ? (
                    <EmptyRow
                      colSpan={4}
                      message="No financial years found"
                    />
                  ) : (
                    financialYears.map((fy, index) => (
                      <TR key={fy.srno}>
                        <TD first>{index + 1}</TD>
                        <TD>{getFYLabel(fy.fys, fy.fye)}</TD>
                        <TD>
                          <Input
                            type="date"
                            showLabel={false}
                            value={fy.acc_locked_upto || ""}
                            min={fy.fys}
                            max={fy.fye}
                            onChange={(e) =>
                              handleFYChange(
                                index,
                                "acc_locked_upto",
                                e.target.value
                              )
                            }
                          />
                        </TD>
                        <TD last>
                          <Input
                            type="date"
                            showLabel={false}
                            value={fy.gst_locked_upto || ""}
                            min={fy.fys}
                            max={fy.fye}
                            onChange={(e) =>
                              handleFYChange(
                                index,
                                "gst_locked_upto",
                                e.target.value
                              )
                            }
                          />
                        </TD>
                      </TR>
                    ))
                  )}
                </TBody>
              </Table>

              {financialYears.length > 0 && (
                <div className="mt-4 flex justify-end">
                  <button
                    type="button"
                    onClick={handleSaveFYLocking}
                    disabled={isSavingFY}
                    className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSavingFY ? "Saving..." : "Save Data Locking"}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
